import React, { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import { useAppStore } from "@src/AppStore";
import { apiOvertimeReasonUpdate } from "@src/apis/labour-reason-update";
import { apiDRSOvertimeFetch } from "@src/apis/drs-overtime-fetch";
import DepotPayrollAutocomplete from "./Filters/DepotPayrollAutocomplete";
import moment from "moment";
import FormModalDatePicker from "@src/components/FormModal/DatePicker";

const reasonCodes = [
  "OT01 – B - Tender Allowance/Increased Productivity (Reduced Shifts)",
  "OT02 - C - Client Scope / Readiness / Instructions",
  "OT03 - C - Client Requirements / Testing",
  "OT04 - UB - Tender / Planning / Productivity Issues",
  "OT05 - UB - Weather / Environmental Conditions",
  "OT06 - UB - Safety Incident / Investigation",
  "OT07 - UB - Plant Production Constraints / Breakdown / Maintenance",
  "OT08 - UB - Logistics Constraints",
  "OT09 - UB - Mobile Plant / Equipment Constraints (On-Site)",
  "OT10 - UB - Labour / Staffing / Training Constraints",
];

const UnallocatedOvertime = () => {
  const { serialId, regionId } = useAppStore();
  const [users, setUsers] = useState<any[]>([]);
  const [selectedReasons, setSelectedReasons] = useState<{ [overtimeId: number]: string }>({});
  const [applyToAllReason, setApplyToAllReason] = useState("");
  const [selectedDepotPayroll, setSelectedDepotPayroll] = useState<{ label: string; value: string }>({ label: "All Depots", value: "__region__" });

  const [startDate, setStartDate] = useState(moment().subtract(7, "days").format("YYYY/MM/DD"));
  const [endDate, setEndDate] = useState(moment().add(1, "days").format("YYYY/MM/DD"));
  const [dateError, setDateError] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("All");

  useEffect(() => {
    async function fetchData() {
      setUsers([]);
      if (moment(endDate, "YYYY/MM/DD").isBefore(moment(startDate, "YYYY/MM/DD"))) return;

      const start = moment(startDate, "YYYY/MM/DD").startOf("day").format("YYYY-MM-DDTHH:mm:ss");
      const end = moment(endDate, "YYYY/MM/DD").endOf("day").format("YYYY-MM-DDTHH:mm:ss");

      const params: any = { start, end };
      if (selectedDepotPayroll.value === "__region__") {
        params.regionId = regionId === "1013" ? "0" : regionId;
      } else {
        params.depotPayroll = selectedDepotPayroll.label;
      }

      const res = await apiDRSOvertimeFetch(params);
      const data = res.data?.labour || [];

      console.log("✅ API fetched users:", data);
      setUsers(data);
    }

    fetchData();
  }, [regionId, serialId, selectedDepotPayroll.value, selectedDepotPayroll.label, startDate, endDate]);

  const handleStartDateChange = (value: string) => {
    setStartDate(value);
    if (moment(endDate, "YYYY/MM/DD").isBefore(moment(value, "YYYY/MM/DD"))) {
      setDateError("End date must be after start date.");
    } else {
      setDateError("");
    }
  };

  const handleEndDateChange = (value: string) => {
    setEndDate(value);
    if (moment(value, "YYYY/MM/DD").isBefore(moment(startDate, "YYYY/MM/DD"))) {
      setDateError("End date must be after start date.");
    } else {
      setDateError("");
    }
  };

  const employeeNames = useMemo(() => {
    const names = Array.from(new Set(users.map((u) => `${u.firstName || ""} ${u.lastName || ""}`))).sort();
    console.log("🧾 Unique employee names:", names);
    return names;
  }, [users]);

  const filteredUsers = useMemo(() => {
    const result = selectedEmployee === "All" ? users : users.filter((u) => `${u.firstName || ""} ${u.lastName || ""}` === selectedEmployee);
    console.log("🧠 Filtered users for:", selectedEmployee, result);
    return result;
  }, [users, selectedEmployee]);

  const handleApplyToAll = () => {
    const updated = Object.fromEntries(filteredUsers.map((u) => [u.overtimeId, applyToAllReason]));
    setSelectedReasons((prev) => ({ ...prev, ...updated }));
  };

  const handleReasonChange = (overtimeId: number, value: string) => {
    setSelectedReasons((prev) => ({
      ...prev,
      [overtimeId]: value,
    }));
  };

  const handleSave = async () => {
    const updates = Object.entries(selectedReasons).map(([id, reasonCode]) => apiOvertimeReasonUpdate({ overtimeId: Number(id), reasonCode, serialId }));
    await Promise.all(updates);
    alert("Saved successfully");
  };

  const totalHours = filteredUsers.reduce((sum, u) => sum + (u.hours || 0), 0);

  return (
    <Wrapper>
      <HeaderBlock>
        <Heading>Unallocated Overtime</Heading>

        <CenterGroup>
          <DepotPayrollContainer>
            <DepotPayrollAutocomplete
              selectedItem={selectedDepotPayroll}
              onSelect={(item) => {
                setSelectedDepotPayroll(item);
                setSelectedEmployee("All");
                console.log("🔁 Depot payroll changed, resetting employee filter.");
              }}
            />
          </DepotPayrollContainer>
        </CenterGroup>

        <TopRow>
          <LeftGroup>
            <FormModalDatePicker label="Start Date" value={startDate} onChange={handleStartDateChange} />
            <FormModalDatePicker label="End Date" value={endDate} onChange={handleEndDateChange} />
            {dateError && <ErrorText>{dateError}</ErrorText>}
          </LeftGroup>

          <RightGroup>
            <Select value={selectedEmployee} onChange={(e) => setSelectedEmployee(e.target.value)}>
              <option value="All">All Employees</option>
              {employeeNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Select>

            <Select value={applyToAllReason} onChange={(e) => setApplyToAllReason(e.target.value)}>
              <option value="">Select reason</option>
              {reasonCodes.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </Select>

            <ApplyButton onClick={handleApplyToAll}>Apply to All</ApplyButton>
          </RightGroup>
        </TopRow>
      </HeaderBlock>

      <Table>
        <thead>
          <tr>
            <th>Employee Name</th>
            <th>Depot Payroll</th>
            <th>Overtime Date</th>
            <th>Overtime Type</th>
            <th>Hours</th>
            <th>Reason</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.map((user, index) => (
            <tr key={`${user.overtimeId}-${index}`}>
              <td>{`${user.firstName || ""} ${user.lastName || ""}`}</td>
              <td>{user.depotPayroll || "-"}</td>
              <td>{new Date(user.overtimeDate).toLocaleDateString()}</td>
              <td>{user.overtimeType}</td>
              <td>{user.hours}</td>
              <td>
                <RowSelect value={selectedReasons[user.overtimeId] || user.overTimeReasonCode || ""} onChange={(e) => handleReasonChange(user.overtimeId, e.target.value)}>
                  <option value="">Select</option>
                  {reasonCodes.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </RowSelect>
              </td>
            </tr>
          ))}

          <tr>
            <td>
              <strong>Total</strong>
            </td>
            <td></td>
            <td></td>
            <td>
              <strong>{totalHours}</strong>
            </td>
            <td></td>
          </tr>
        </tbody>
      </Table>

      <BottomRow>
        <SaveButton onClick={handleSave}>Save</SaveButton>
      </BottomRow>
    </Wrapper>
  );
};

const ErrorText = styled.div`
  color: red;
  font-size: 14px;
  margin-top: 4px;
  width: 100%;
`;

const Wrapper = styled.div`
  margin-top: 20px;
  min-height: 500px;
  width: 100%;
`;

const Heading = styled.h2`
  font-weight: bold;
  margin-bottom: 20px;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 20px;
  width: 100%;
  gap: 20px;
`;

const LeftGroup = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
`;

const RightGroup = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: flex-end;
`;

const CenterGroup = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0;
`;

const Select = styled.select`
  padding: 5px;
  min-width: 250px;
  height: 38px;
`;

const RowSelect = styled.select`
  padding: 5px;
  width: 100%;
`;

const ApplyButton = styled.button`
  background-color: #0a9688;
  color: white;
  padding: 5px 10px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  height: 38px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    border: 1px solid #ddd;
    padding: 8px;
  }
  th {
    background-color: #0a9688;
    color: white;
  }
`;

const BottomRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;

const SaveButton = styled.button`
  background-color: #0a9688;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  height: 38px;
`;

const DepotPayrollContainer = styled.div`
  select {
    background-color: #0a9688;
    color: white;
    border: none;
    border-radius: 5px;
    padding: 5px 10px;
    height: 38px;
    cursor: pointer;
    min-width: 250px;
    width: 100%;
  }
`;

const HeaderBlock = styled.div`
  width: 100%;
  padding: 0 40px;
  box-sizing: border-box;
`;

export default UnallocatedOvertime;
